// Secciones compartidas para las 5 variantes editoriales (propuesta 1).
// Las variantes reusan Hero/Origin/Catalog/Quality/Footer y solo cambian
// la sección de SEGMENTOS y la de LOGÍSTICA (los dos puntos a iterar).
import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMugHot, faUtensils, faHotel, faCalendarDays, faLock,
} from '@fortawesome/free-solid-svg-icons';
import {
  VARIEDADES_CAFE, ORIGEN, PILARES_CALIDAD, DEFECTOS, URL_REGULACIONES, IMAGENES, SEGMENTOS,
} from '../data/cafes';

// Roles de color (un único acento interactivo, lo demás estructural/decorativo):
//   verde       → color estructural de marca: headings, números de pilar, fondos de sección.
//   bronce      → ÚNICO acento interactivo: CTAs, links, eyebrows, precios.
//   doradoClaro → sólo decorativo SOBRE fondos oscuros (eyebrow del hero, footer).
//   verdeOscuro → fondos oscuros (footer). tinta/suave → texto. crema/borde → neutros.
export const C = {
  verde: '#2E5B2E',
  verdeOscuro: '#1F3F1F',
  bronce: '#8A5A2B',
  doradoClaro: '#E7C9A0',
  tinta: '#2b2b2b',
  suave: '#5A5A52',
  crema: '#F8F6F1',
  borde: '#E4E2D6',
};

// Mapa de claves de ícono (en data/cafes.js) a íconos FontAwesome.
const SEG_ICON = {
  'mug-hot': faMugHot,
  utensils: faUtensils,
  hotel: faHotel,
  'calendar-days': faCalendarDays,
};
const serif = "'Playfair Display', Georgia, serif";
const ORIGEN_IMG = IMAGENES.granoMano;

// CSS compartido (clases .xv-*). Se inyecta una vez por variante.
export const PARTS_CSS = `
  .xv-root h1, .xv-root h2, .xv-root h3 { font-family: ${serif}; }
  .xv-cta { display:inline-block; background:${C.bronce}; color:#fff; padding:15px 34px; border-radius:8px; font-weight:700; letter-spacing:.5px; text-decoration:none; transition: background .25s ease, transform .25s ease; }
  .xv-cta:hover { background:#6f4720; transform: translateY(-2px); }
  .xv-ghost { display:inline-block; color:#fff; border:1px solid rgba(255,255,255,.6); padding:14px 30px; border-radius:8px; font-weight:700; text-decoration:none; transition: background .25s ease; }
  .xv-ghost:hover { background: rgba(255,255,255,.12); }
  .xv-link { color:${C.bronce}; font-weight:700; }
  .xv-card, .xv-seg { transition: transform .25s ease, box-shadow .25s ease; }
  .xv-card:hover, .xv-seg:hover { transform: translateY(-4px); box-shadow: 0 16px 38px rgba(46,91,46,.14); }
  .xv-cafe { display:grid; grid-template-columns:1.1fr 1fr; gap:36px; align-items:center; padding:34px 0; border-top:1px solid ${C.borde}; }
  .xv-cafe:nth-child(even) .xv-cafe-media { order:2; }
  a:focus-visible, .xv-cta:focus-visible, [tabindex]:focus-visible { outline:3px solid ${C.bronce}; outline-offset:3px; border-radius:6px; }
  @media (prefers-reduced-motion: no-preference) {
    .xv-fade { opacity:0; transform: translateY(18px); animation: xvin .9s cubic-bezier(.16,1,.3,1) forwards; }
    .xv-bar { animation: xvbar 1s cubic-bezier(.16,1,.3,1); }
    @keyframes xvin { to { opacity:1; transform:none; } }
    @keyframes xvbar { from { width:0 !important; } }
  }
  @media (prefers-reduced-motion: reduce) { .xv-card, .xv-seg, .xv-cta { transition:none !important; } }
  @media (max-width: 860px) {
    .xv-2col, .xv-grid, .xv-raz, .xv-pilares, .xv-segs { grid-template-columns: 1fr !important; }
    .xv-cafe { grid-template-columns: 1fr !important; gap:18px; }
    .xv-cafe:nth-child(even) .xv-cafe-media { order:0; }
    .xv-navlinks { display:none !important; }
    .xv-hero-h1 { font-size: 2.6rem !important; }
    .xv-pad { padding-left:22px !important; padding-right:22px !important; }
  }
`;

// Perfil sensorial en barras etiquetadas (Cuerpo / Acidez / Dulzor).
export function Barras({ g }) {
  const filas = [
    { l: 'Cuerpo', v: g.cuerpo, c: C.verde },
    { l: 'Acidez', v: g.acidez, c: C.bronce },
    { l: 'Dulzor', v: g.dulzor, c: '#A98248' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {filas.map((f) => (
        <div key={f.l} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '11px', color: C.suave, width: '54px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '.5px' }}>{f.l}</span>
          <div style={{ flex: 1, height: '7px', background: C.borde, borderRadius: '4px', overflow: 'hidden' }}>
            <div className="xv-bar" style={{ width: `${f.v}%`, height: '100%', background: f.c, borderRadius: '4px' }} />
          </div>
          <span style={{ fontSize: '11px', color: C.suave, width: '26px', flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{f.v}</span>
        </div>
      ))}
    </div>
  );
}

export function Nav() {
  return (
    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '22px 7vw', position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5 }}>
      <span style={{ fontFamily: serif, color: '#fff', fontSize: '1.5rem', fontWeight: 800, letterSpacing: '1px' }}>Xavana Café</span>
      <nav className="xv-navlinks" style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
        <Link to="/contacto" style={{ color: '#fff', textDecoration: 'none', fontWeight: 600 }}>Contacto</Link>
        <Link to="/login" style={{ color: C.verdeOscuro, background: '#fff', padding: '9px 20px', borderRadius: '8px', textDecoration: 'none', fontWeight: 700 }}>Acceso Clientes</Link>
      </nav>
    </header>
  );
}

export function Hero({ img, eyebrow, titulo, subtitulo, center, primary, secondary }) {
  const overlay = center
    ? 'linear-gradient(rgba(20,28,18,.74), rgba(20,28,18,.82))'
    : 'linear-gradient(90deg, rgba(20,28,18,.84), rgba(20,28,18,.4))';
  return (
    <section className="xv-pad" style={{
      position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column', justifyContent: 'center',
      alignItems: center ? 'center' : 'flex-start', textAlign: center ? 'center' : 'left', padding: '0 7vw',
      backgroundImage: `${overlay}, url('${img}')`, backgroundSize: 'cover', backgroundPosition: 'center',
    }}>
      <div className="xv-fade" style={{ maxWidth: center ? '820px' : '720px', color: '#fff' }}>
        <span style={{ display: 'inline-block', color: C.doradoClaro, letterSpacing: '3px', textTransform: 'uppercase', fontSize: '.8rem', fontWeight: 700, marginBottom: '20px' }}>{eyebrow}</span>
        <h1 className="xv-hero-h1" style={{ fontSize: 'clamp(2.6rem, 6.3vw, 4.7rem)', lineHeight: 1.04, marginBottom: '22px', textWrap: 'balance' }}>{titulo}</h1>
        <p style={{ fontSize: '1.2rem', lineHeight: 1.6, maxWidth: '62ch', margin: center ? '0 auto 34px' : '0 0 34px', color: '#ece8e1' }}>{subtitulo}</p>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: center ? 'center' : 'flex-start' }}>
          {primary && (primary.to ? <Link to={primary.to} className="xv-cta">{primary.label}</Link> : <a href={primary.href} className="xv-cta">{primary.label}</a>)}
          {secondary && (secondary.to ? <Link to={secondary.to} className="xv-ghost">{secondary.label}</Link> : <a href={secondary.href} className="xv-ghost">{secondary.label}</a>)}
        </div>
      </div>
    </section>
  );
}

export function Origin({ imageRight }) {
  const Img = (
    <img className="xv-img" src={ORIGEN_IMG} alt="Granos de café de Xicotepec de Juárez, Sierra Norte de Puebla" loading="lazy"
      style={{ width: '100%', maxHeight: '440px', objectFit: 'cover', borderRadius: '16px', boxShadow: '0 18px 44px rgba(31,63,31,.16)' }} />
  );
  const Texto = (
    <div>
      <p style={{ color: C.bronce, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '12px', margin: '0 0 10px' }}>{ORIGEN.lugar} · {ORIGEN.region}</p>
      <h2 style={{ fontSize: 'clamp(1.9rem, 3.6vw, 2.6rem)', color: C.verde, margin: '0 0 16px', lineHeight: 1.12 }}>{ORIGEN.titulo}</h2>
      <p style={{ color: C.tinta, fontSize: '1.05rem', lineHeight: 1.7, margin: '0 0 26px' }}>{ORIGEN.resumen}</p>
      <div className="xv-raz" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {ORIGEN.razones.map((r) => (
          <div key={r.titulo}>
            <h3 style={{ fontWeight: 700, fontSize: '1rem', color: C.verde, margin: '0 0 6px', display: 'flex', gap: '8px', alignItems: 'center', fontFamily: "'Lato', sans-serif" }}>
              <span aria-hidden="true" style={{ color: C.bronce }}>•</span>{r.titulo}
            </h3>
            <p style={{ color: C.suave, fontSize: '.9rem', lineHeight: 1.55, margin: 0 }}>{r.texto}</p>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <section id="origen" className="xv-pad" style={{ padding: '90px 7vw' }}>
      <div className="xv-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '52px', alignItems: 'center' }}>
        {imageRight ? <>{Texto}{Img}</> : <>{Img}{Texto}</>}
      </div>
    </section>
  );
}

// Chips de tipo + tueste, reutilizados en ambos formatos de tarjeta.
function CafeChips({ c }) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.5px' }}>
      <span style={{ background: '#eef2ee', color: C.verde, padding: '4px 10px', borderRadius: '20px', fontWeight: 700 }}>{c.tipo}</span>
      <span style={{ background: '#f3ece2', color: C.bronce, padding: '4px 10px', borderRadius: '20px', fontWeight: 700 }}>Tueste {c.tueste}</span>
    </div>
  );
}

export function Catalog({ bg }) {
  return (
    <section id="catalogo" className="xv-pad" style={{ background: bg || '#fff', padding: '80px 7vw' }}>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', color: C.verde, margin: 0 }}>Nuestros cafés</h2>
      </div>
      <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
        {VARIEDADES_CAFE.map((c) => (
          <article key={c.id} className="xv-cafe">
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <h3 style={{ fontSize: '2rem', color: C.verde, lineHeight: 1.1 }}>{c.nombre}</h3>
              </div>
              <p style={{ fontStyle: 'italic', color: C.bronce, margin: '6px 0 12px' }}>{c.eslogan}</p>
              <p style={{ color: C.suave, lineHeight: 1.6, marginBottom: '16px', maxWidth: '52ch' }}>{c.perfil}</p>
              <CafeChips c={c} />
            </div>
            <div className="xv-cafe-media xv-card" style={{ background: '#fff', borderRadius: '14px', padding: '26px', boxShadow: '0 12px 30px rgba(46,91,46,.10)' }}>
              <span style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '1px', color: '#8a857d', fontWeight: 700 }}>Perfil sensorial</span>
              <div style={{ marginTop: '16px' }}><Barras g={c.graficos} /></div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// Calidad: pilares numerados + tolerancia con TABLA (la tabla preferida).
export function Quality({ bg }) {
  return (
    <section className="xv-pad" style={{ background: bg || '#fff', padding: '80px 7vw' }}>
      <div style={{ textAlign: 'center', marginBottom: '44px' }}>
        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.6rem)', color: C.verde, margin: 0 }}>Cómo garantizamos cada lote</h2>
      </div>
      <div className="xv-pilares" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '22px', maxWidth: '1180px', margin: '0 auto 56px' }}>
        {PILARES_CALIDAD.map((p, i) => (
          <article key={p.titulo} style={{ background: C.crema, border: `1px solid ${C.borde}`, borderRadius: '14px', padding: '24px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '999px', background: C.verde, color: '#fff', fontWeight: 700, marginBottom: '14px' }}>{i + 1}</span>
            <h3 style={{ fontSize: '1.12rem', color: C.verde, margin: '0 0 8px' }}>{p.titulo}</h3>
            <p style={{ color: C.suave, fontSize: '.92rem', lineHeight: 1.6, margin: 0 }}>{p.texto}</p>
          </article>
        ))}
      </div>
      <div className="xv-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'center', maxWidth: '1180px', margin: '0 auto' }}>
        <div>
          <h3 style={{ fontSize: '1.5rem', color: C.verde, margin: '0 0 14px' }}>Tolerancia de defectos por criba de 350 g</h3>
          <p style={{ color: C.suave, fontSize: '.95rem', lineHeight: 1.6, margin: '0 0 18px' }}>Cada mezcla responde a un estándar estricto de defectos máximos. A menor tolerancia, mayor selección del grano.</p>
          <a href={URL_REGULACIONES} target="_blank" rel="noopener noreferrer" className="xv-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', textDecoration: 'underline' }}>
            Regulaciones Oficiales MX <span aria-hidden="true">↗</span>
          </a>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: `1px solid ${C.borde}`, borderRadius: '12px', overflow: 'hidden' }}>
            <caption style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Defectos máximos permitidos por nivel de mezcla</caption>
            <thead>
              <tr style={{ background: C.verde, color: '#fff' }}>
                <th scope="col" style={{ textAlign: 'left', padding: '12px 16px', fontSize: '.9rem' }}>Nivel</th>
                <th scope="col" style={{ textAlign: 'center', padding: '12px 16px', fontSize: '.9rem' }}>Defectos</th>
                <th scope="col" style={{ textAlign: 'left', padding: '12px 16px', fontSize: '.9rem' }}>Nota</th>
              </tr>
            </thead>
            <tbody>
              {DEFECTOS.map((d, i) => (
                <tr key={d.tier} style={{ background: i % 2 ? C.crema : '#fff' }}>
                  <th scope="row" style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 700, color: C.tinta, fontSize: '.92rem' }}>{d.tier}</th>
                  <td style={{ textAlign: 'center', padding: '12px 16px', fontFamily: serif, fontWeight: 800, fontSize: '1.3rem', color: C.bronce }}>{d.valor}</td>
                  <td style={{ textAlign: 'left', padding: '12px 16px', color: C.suave, fontSize: '.85rem' }}>{d.nota}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export function Segmentos() {
  return (
    <section className="xv-pad" style={{ background: '#fff', padding: '80px 7vw' }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.6rem)', color: C.verde, margin: 0 }}>Pensado para tu operación</h2>
      </div>
      <div className="xv-segs" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', maxWidth: '980px', margin: '0 auto' }}>
        {SEGMENTOS.map((s) => (
          <div key={s.id} className="xv-seg" style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: '20px', alignItems: 'flex-start', background: C.crema, border: `1px solid ${C.borde}`, borderRadius: '16px', padding: '28px 24px' }}>
            <div style={{ width: '52px', height: '52px', background: '#fff', border: `1px solid ${C.borde}`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden="true">
              <FontAwesomeIcon icon={SEG_ICON[s.icono] || faMugHot} style={{ fontSize: '1.4rem', color: C.verde }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <h3 style={{ fontSize: '1.2rem', color: C.verde, margin: 0 }}>{s.nombre}</h3>
                <span style={{ color: C.bronce, fontWeight: 700, fontSize: '.82rem' }}>{s.promesa}</span>
              </div>
              <p style={{ color: C.suave, lineHeight: 1.55, fontSize: '.95rem', margin: 0 }}>{s.texto}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Footer({ onAgente }) {
  return (
    <footer style={{ background: C.verdeOscuro, color: '#e7eee7', padding: '50px 7vw', textAlign: 'center' }}>
      <div style={{ fontFamily: serif, fontSize: '1.6rem', fontWeight: 800, marginBottom: '10px' }}>Xavana Café</div>
      <p style={{ marginBottom: '8px' }}>Santiago de Querétaro, Querétaro | Xicotepec de Juárez, Puebla</p>
      <p style={{ fontSize: '.85rem', marginBottom: onAgente ? '20px' : 0 }}>
        <a href={URL_REGULACIONES} target="_blank" rel="noopener noreferrer" style={{ color: C.doradoClaro, textDecoration: 'none', fontWeight: 700 }}>Marco Normativo y Regulaciones de Exportación (SADER)</a>
      </p>
      {onAgente && (
        <button
          onClick={onAgente}
          aria-label="Cotización por agente (requiere PIN)"
          style={{
            background: 'transparent', color: C.doradoClaro, border: `1px solid ${C.doradoClaro}`,
            borderRadius: '50px', padding: '10px 22px', fontWeight: 700, fontSize: '13px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'background .2s ease, color .2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.doradoClaro; e.currentTarget.style.color = C.verdeOscuro; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.doradoClaro; }}
        >
          <FontAwesomeIcon icon={faLock} /> Cotización por agente
        </button>
      )}
    </footer>
  );
}
